import { ModalsWrapperContext, useToast } from "@kanda-libs/ks-design-library";
import { actions, type Lead } from "@kanda-libs/ks-frontend-services";
import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { selectors } from "store";
import { useAppDispatch } from "components/shared/App";
import { MODAL_ID as CREATE_JOB_FROM_LEAD_MODAL_ID } from "./CreateJobFromLeadModal/constants";
import { MODAL_ID as CLOSE_LEAD_MODAL_ID } from "./CloseLeadModal/constants";

export interface LeadActionsHook {
  lead?: Lead;
  isLoading: boolean;
  onCreateJobFromLead: (cid: string) => void;
  onCloseLead: () => void;
  onClickCreateJobFromLead: () => void;
  onClickCloseLead: () => void;
}

export default function useLeadActions(): LeadActionsHook {
  const dispatch = useAppDispatch();

  const { showModal, hideModal } = useContext(ModalsWrapperContext);
  const { showSuccess, showError } = useToast();
  const lead = useSelector(selectors.lead.getEntity);
  const isLoading = useSelector(selectors.getIsLoading);
  const isSubmitting = useSelector(selectors.lead.getIsSubmitting);

  const onCreateJobFromLead = useCallback(
    (cid: string) => {
      if (!lead?.id) return;

      dispatch(
        actions.referLead({
          params: {
            id: lead.id,
          },
          body: {
            cid,
          },
          onSuccess: () => {
            showSuccess("Created lead from job successfully");
            hideModal(CREATE_JOB_FROM_LEAD_MODAL_ID);
          },
          onError: () => {
            showError("Error creating lead from job");
          },
        })
      );
    },
    [lead]
  );

  const onCloseLead = useCallback(() => {
    if (!lead?.id) return;

    hideModal(CLOSE_LEAD_MODAL_ID);

    dispatch(
      actions.putLead({
        params: {
          id: lead.id,
        },
        body: {
          ...lead,
          status: "closed",
        },
        onSuccess: () => {
          showSuccess("Lead closed");
        },
        onError: () => {
          showError("Error closing lead from job");
        },
      })
    );
  }, [lead]);

  const onClickCreateJobFromLead = () => {
    showModal(CREATE_JOB_FROM_LEAD_MODAL_ID);
  };

  const onClickCloseLead = () => {
    showModal(CLOSE_LEAD_MODAL_ID);
  };

  return {
    lead,
    isLoading: isLoading || isSubmitting,
    onCreateJobFromLead,
    onCloseLead,
    onClickCreateJobFromLead,
    onClickCloseLead,
  };
}
